import React, { useState, useEffect } from 'react';
import { Link, useBeforeUnload } from 'react-router-dom';
import './SetupScreen.css';
import { CustomPrompt } from '../types/prompts';

const DEFAULT_SUMMARIZE_PROMPT =
    'Please provide a concise summary of this transcription session, highlighting key discussion points and any important decisions or outcomes.';
const DEFAULT_QUOTES_PROMPT =
    'Extract three significant quotes from the transcript that best represent key insights or decisions. Include speaker attribution if available.';

interface SetupScreenProps {
    onComplete: () => void;
}

const SetupScreen: React.FC<SetupScreenProps> = ({ onComplete }) => {
    const [storageAccountName, setStorageAccountName] = useState('');
    const [containerName, setContainerName] = useState('');
    const [chatGPTKey, setChatGPTKey] = useState('');
    const [summarizePrompt, setSummarizePrompt] = useState(DEFAULT_SUMMARIZE_PROMPT);
    const [quotesPrompt, setQuotesPrompt] = useState(DEFAULT_QUOTES_PROMPT);
    const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
    const [newPromptName, setNewPromptName] = useState('');
    const [newPromptText, setNewPromptText] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editText, setEditText] = useState('');

    // Load saved credentials from cookies
    useEffect(() => {
        // Load all settings on mount
        const cookies = document.cookie.split('; ');
        const getCookieValue = (name: string) => {
            const cookie = cookies.find((row) => row.startsWith(`${name}=`));
            if (!cookie) return '';
            const value = cookie.split('=')[1];
            try {
                return decodeURIComponent(value);
            } catch {
                return value;
            }
        };
        
        // Load custom prompts
        const savedPrompts = getCookieValue('customPrompts');
        if (savedPrompts) {
            try {
                setCustomPrompts(JSON.parse(savedPrompts));
            } catch (e) {
                console.error('Failed to parse custom prompts:', e);
            }
        }
        
        const loadedStorageAccount = getCookieValue('storageAccountName');
        const loadedContainer = getCookieValue('containerName');
        const loadedChatGPTKey = getCookieValue('chatGPTKey');
        const loadedSummarizePrompt = getCookieValue('summarizePrompt');
        const loadedQuotesPrompt = getCookieValue('quotesPrompt');

        if (loadedStorageAccount) setStorageAccountName(loadedStorageAccount);
        if (loadedContainer) setContainerName(loadedContainer);
        if (loadedChatGPTKey) setChatGPTKey(loadedChatGPTKey);
        if (loadedSummarizePrompt) setSummarizePrompt(loadedSummarizePrompt);
        if (loadedQuotesPrompt) setQuotesPrompt(loadedQuotesPrompt);
    }, []);

    // Auto-save whenever settings change
    useEffect(() => {
        const saveSettings = () => {
            document.cookie = `storageAccountName=${storageAccountName}; path=/;`;
            document.cookie = `containerName=${containerName}; path=/;`;
            document.cookie = `chatGPTKey=${chatGPTKey}; path=/;`;
            document.cookie = `summarizePrompt=${summarizePrompt}; path=/;`;
            document.cookie = `quotesPrompt=${quotesPrompt}; path=/;`;
        };

        saveSettings();
    }, [storageAccountName, containerName, chatGPTKey, summarizePrompt, quotesPrompt]);

    // Auto-save whenever custom prompts change
    useEffect(() => {
        if (customPrompts.length > 0) {
            document.cookie = `customPrompts=${encodeURIComponent(JSON.stringify(customPrompts))}; path=/;`;
        }
    }, [customPrompts]);

    const validateInputs = () => {
        if (!storageAccountName) {
            setError('Storage Account Name is required.');
            return false;
        }
        if (!containerName) {
            setError('Container Name is required.');
            return false;
        }
        setError(null);
        return true;
    };

    const addCustomPrompt = () => {
        if (!newPromptName || !newPromptText) return;
        const newPrompt: CustomPrompt = {
            id: Date.now().toString(),
            name: newPromptName,
            prompt: newPromptText
        };
        setCustomPrompts([...customPrompts, newPrompt]);
        setNewPromptName('');
        setNewPromptText('');
    };

    const removeCustomPrompt = (id: string) => {
        setCustomPrompts(customPrompts.filter(p => p.id !== id));
    };

    const startEdit = (prompt: CustomPrompt) => {
        setEditingPromptId(prompt.id);
        setEditName(prompt.name);
        setEditText(prompt.prompt);
    };

    const saveEdit = () => {
        if (!editingPromptId) return;
        setCustomPrompts(prompts => 
            prompts.map(p => 
                p.id === editingPromptId 
                    ? { ...p, name: editName, prompt: editText }
                    : p
            )
        );
        setEditingPromptId(null);
        setEditName('');
        setEditText('');
    };

    const cancelEdit = () => {
        setEditingPromptId(null);
        setEditName('');
        setEditText('');
    };

    return (
        <div className="setup-container">
            <div className="setup-content">
                <div className="setup-header">
                    <h1 className="setup-title">Configure Settings</h1>
                </div>
                <div className="setup-form">
                    <div className="setup-section">
                        <h2 className="setup-section-title">Azure Storage Settings</h2>
                        <div className="input-group">
                            <label htmlFor="storageAccount">Storage Account Name</label>
                            <input
                                id="storageAccount"
                                type="text"
                                className="setup-input"
                                value={storageAccountName}
                                onChange={(e) => setStorageAccountName(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="containerName">Container Name</label>
                            <input
                                id="containerName"
                                type="text"
                                className="setup-input"
                                value={containerName}
                                onChange={(e) => setContainerName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="setup-section">
                        <h2 className="setup-section-title">AI Analysis Settings</h2>
                        <div className="input-group">
                            <label htmlFor="apiKey">OpenAI API Key</label>
                            <input
                                id="apiKey"
                                type="password"
                                className="setup-input"
                                value={chatGPTKey}
                                onChange={(e) => setChatGPTKey(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="summarizePrompt">Summarize Prompt</label>
                            <textarea
                                id="summarizePrompt"
                                className="setup-textarea"
                                value={summarizePrompt}
                                onChange={(e) => setSummarizePrompt(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="quotesPrompt">Quotes Prompt</label>
                            <textarea
                                id="quotesPrompt"
                                className="setup-textarea"
                                value={quotesPrompt}
                                onChange={(e) => setQuotesPrompt(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="setup-section">
                        <h2 className="setup-section-title">Custom Prompts</h2>
                        <div className="custom-prompts-list">
                            {customPrompts.map(prompt => (
                                <div key={prompt.id} className="custom-prompt-item">
                                    {editingPromptId === prompt.id ? (
                                        <div className="prompt-edit-form">
                                            <input
                                                type="text"
                                                className="setup-input"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                placeholder="Prompt Name"
                                            />
                                            <textarea
                                                className="setup-textarea"
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                placeholder="Prompt Text"
                                            />
                                            <div className="prompt-edit-actions">
                                                <button onClick={saveEdit} className="save-prompt-btn">
                                                    Save
                                                </button>
                                                <button onClick={cancelEdit} className="cancel-prompt-btn">
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="prompt-header">
                                                <h3>{prompt.name}</h3>
                                                <div className="prompt-actions">
                                                    <button 
                                                        onClick={() => startEdit(prompt)}
                                                        className="edit-prompt-btn"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button 
                                                        onClick={() => removeCustomPrompt(prompt.id)}
                                                        className="remove-prompt-btn"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                            <p className="prompt-text">{prompt.prompt}</p>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="add-prompt-form">
                            <input
                                type="text"
                                className="setup-input"
                                placeholder="Prompt Name"
                                value={newPromptName}
                                onChange={(e) => setNewPromptName(e.target.value)}
                            />
                            <textarea
                                className="setup-textarea"
                                placeholder="Prompt Text"
                                value={newPromptText}
                                onChange={(e) => setNewPromptText(e.target.value)}
                            />
                            <button
                                onClick={addCustomPrompt}
                                className="add-prompt-btn"
                            >
                                Add Prompt
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetupScreen;
